import { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { type ColumnDef } from '@tanstack/react-table';
import {
  ArrowLeft,
  Brain,
  Send,
  Minimize2,
  GitFork,
  Share2,
  CheckCircle2,
  Paperclip,
  X,
  ChevronDown,
  Loader2,
  MessageSquare,
  History,
  Layers,
  Package,
} from 'lucide-react';
import { format } from 'date-fns';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { DataTable } from '@/components/shared/DataTable';
import { LoadingSpinner } from '@/components/shared/LoadingSpinner';
import { JsonViewer } from '@/components/shared/JsonViewer';
import { useToast } from '@/components/shared/Toast';
import { subscribeAgentStream, ThinkingBuffer } from '@/lib/agentStream';
import { messageDisplay } from '@/lib/messageDisplay';
import type { ThinkingBufferState } from '@/lib/agentStream';
import { JsonField, tryParseJson } from '@/components/agents/JsonField';
import { ConversationControls } from '@/components/agents/ConversationControls';
import {
  useGetConversationQuery,
  useAddConversationMessageMutation,
  useCompactConversationMutation,
  useForkConversationMutation,
  useDelegateConversationMutation,
  useSetConversationModelMutation,
  useCompleteConversationMutation,
} from '@/store/api/agentRuntimeApi';
import { useGetAgentFactoriesQuery, useGetAgentModelsQuery } from '@/store/api/agentFactoryApi';
import {
  useGetStorageCollectionsQuery,
  useGetStorageFilesQuery,
} from '@/store/api/apiSlice';
import type {
  AgentMessage,
  AgentMessageFileRef,
  Conversation,
  ConversationStatus,
  MessageVersion,
  Run,
  RunStatus,
} from '@/types';

// ─── helpers ─────────────────────────────────────────────────────────────────

const STATUS_VARIANT: Record<ConversationStatus, 'success' | 'secondary' | 'warning' | 'destructive' | 'outline'> = {
  pending: 'warning',
  running: 'outline',
  idle: 'secondary',
  paused: 'warning',
  stopped: 'secondary',
  completed: 'success',
  error: 'destructive',
};

const RUN_VARIANT: Record<RunStatus, 'success' | 'secondary' | 'warning' | 'destructive' | 'outline'> = {
  running: 'outline',
  completed: 'success',
  failed: 'destructive',
  aborted: 'secondary',
};

function toolCallInfo(msg: AgentMessage): { name?: string; args: unknown; output: unknown } {
  const c = msg.content as Record<string, unknown> | string | null | undefined;
  if (typeof c === 'object' && c) {
    return {
      name: (c.toolName as string) ?? (c.name as string) ?? (c.type as string),
      args: (c.input ?? c.args) as unknown,
      output: (msg.output ?? (c.output as unknown)) as unknown,
    };
  }
  return { name: undefined, args: c, output: msg.output };
}

function formatTokens(n: number | undefined): string {
  if (n == null) return '—';
  return n.toLocaleString();
}

// ─── message bubble ──────────────────────────────────────────────────────────

function MessageBubble({ msg }: { msg: AgentMessage }) {
  const { text, structured } = messageDisplay(msg.content);

  if (msg.kind === 'tool') {
    const info = toolCallInfo(msg);
    return (
      <div className="max-w-full sm:max-w-[85%]">
        <details className="group rounded-lg border bg-muted/40 p-2 text-sm">
          <summary className="flex cursor-pointer list-none items-center gap-2 text-muted-foreground">
            <ChevronDown className="h-3.5 w-3.5 transition-transform group-open:rotate-180" />
            <span className="font-mono text-xs font-medium">{info.name ?? 'tool'}</span>
            <Badge variant="outline" className="ml-auto text-xs">tool-call</Badge>
          </summary>
          <div className="space-y-2 pt-2">
            <div>
              <p className="mb-1 text-xs text-muted-foreground">Input</p>
              <JsonViewer data={info.args ?? {}} initialExpanded={false} className="[&_pre]:max-h-40" />
            </div>
            {info.output !== undefined && (
              <div>
                <p className="mb-1 text-xs text-muted-foreground">Output</p>
                <JsonViewer data={info.output} initialExpanded={false} className="[&_pre]:max-h-40" />
              </div>
            )}
          </div>
        </details>
      </div>
    );
  }

  const align = msg.kind === 'user' ? 'justify-end' : 'justify-start';
  const bubbleClass =
    msg.kind === 'user'
      ? 'bg-primary text-primary-foreground'
      : msg.kind === 'system'
        ? 'border bg-muted/60 text-muted-foreground italic'
        : 'border bg-card';
  const statusIcon =
    msg.status === 'processing' ? (
      <Loader2 className="h-3 w-3 animate-spin" />
    ) : msg.status === 'failed' ? (
      <X className="h-3 w-3 text-destructive" />
    ) : null;

  return (
    <div className={`flex ${align}`}>
      <div className={`max-w-full rounded-lg px-3 py-2 text-sm ${bubbleClass} sm:max-w-[80%]`}>
        <div className="mb-1 flex items-center gap-1.5">
          <span className="text-[10px] font-semibold uppercase tracking-wide opacity-60">
            {msg.kind}
          </span>
          {statusIcon}
          <span className="ml-auto text-[10px] tabular-nums text-muted-foreground">
            #{msg.seq}
          </span>
        </div>
        <div
          className={
            msg.kind === 'user'
              ? 'whitespace-pre-wrap break-words text-primary-foreground'
              : 'whitespace-pre-wrap break-words'
          }
        >
          {text}
        </div>
        {structured && msg.output !== undefined && (
          <div className="mt-2">
            <p className="mb-1 text-xs text-muted-foreground">Structured output</p>
            <JsonViewer data={msg.output} initialExpanded={false} className="[&_pre]:max-h-48" />
          </div>
        )}
        {msg.fileRefs && msg.fileRefs.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-1">
            {msg.fileRefs.map((f, i) => (
              <span key={i} className="rounded bg-muted px-1.5 py-0.5 font-mono text-[10px]">
                📎 {f.fileId.slice(0, 8)}… {f.mediaType}
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── delegate targets builder ────────────────────────────────────────────────

interface DelegateRow {
  key: string;
  factoryId: string;
  payload: string;
}

let delegateKey = 0;
const newDelegateRow = (): DelegateRow => ({
  key: `target-${++delegateKey}`,
  factoryId: '',
  payload: '',
});

// ─── page ────────────────────────────────────────────────────────────────────

export function ConversationDetailPage() {
  const { conversationId } = useParams<{ conversationId: string }>();
  const id = conversationId ?? '';
  const navigate = useNavigate();
  const { success: toastSuccess, error: toastError } = useToast();

  const { data, isLoading, isError, refetch } = useGetConversationQuery(
    { id, include: 'messages,runs,versions' },
    { skip: !id, refetchOnMountOrArgChange: true },
  );
  const conversation: Conversation | undefined = data?.conversation;
  const messages = useMemo(
    () => [...(data?.messages ?? [])].sort((a, b) => a.seq - b.seq),
    [data],
  );
  const runs = data?.runs ?? [];
  const versions = data?.versions ?? [];

  const refetchRef = useRef(refetch);
  useEffect(() => {
    refetchRef.current = refetch;
  }, [refetch]);

  // ── Thinking / reasoning stream (AC-13) ────────────────────────────────
  // The ThinkingBuffer keys text/reasoning/tokens by step index and is
  // idempotent against the gateway bridge replay (persisted parts re-sent
  // when a client joins the room mid-run).
  const bufferRef = useRef<ThinkingBuffer | null>(null);
  const [thinking, setThinking] = useState<ThinkingBufferState>({
    step: null,
    maxSteps: null,
    text: '',
    reasoning: '',
    tokens: 0,
  });
  const [thinkingOpen, setThinkingOpen] = useState(false);
  const [hasReasoning, setHasReasoning] = useState(false);
  const [wsConnected, setWsConnected] = useState<boolean>(false);
  const [wsStateLabel, setWsStateLabel] = useState<string>('connecting');

  useEffect(() => {
    if (!id) return;
    const buffer = new ThinkingBuffer();
    bufferRef.current = buffer;
    setThinking(buffer.state());
    setHasReasoning(false);
    setThinkingOpen(false);

    const stop = subscribeAgentStream(id, {
      onPart: (part) => {
        if (part.type === 'reasoning-delta' || part.type === 'step-start') {
          setHasReasoning(true);
          setThinkingOpen(true);
        }
        buffer.addPart(part);
        setThinking(buffer.state());
      },
      onSubscription: () => {
        setWsConnected(true);
        setWsStateLabel('connected');
        // Persistence is the source of truth — refetch on (re)connect.
        refetchRef.current();
      },
      onDisconnect: () => {
        setWsConnected(false);
        setWsStateLabel('reconnecting');
      },
      onError: () => setWsStateLabel('stream error'),
    });

    return () => {
      stop();
      buffer.reset();
    };
  }, [id]);

  // ── Mutations ──────────────────────────────────────────────────────────
  const [addMessage, { isLoading: sending }] = useAddConversationMessageMutation();
  const [compactConversation, { isLoading: compacting }] = useCompactConversationMutation();
  const [forkConversation, { isLoading: forking }] = useForkConversationMutation();
  const [delegateConversation, { isLoading: delegating }] = useDelegateConversationMutation();
  const [setConversationModel] = useSetConversationModelMutation();
  const [completeConversation, { isLoading: completing }] = useCompleteConversationMutation();

  const { data: models } = useGetAgentModelsQuery();
  const { data: factories } = useGetAgentFactoriesQuery();

  // ── Send state ─────────────────────────────────────────────────────────
  const [sendText, setSendText] = useState('');
  const [fileRefs, setFileRefs] = useState<AgentMessageFileRef[]>([]);
  const [filePickerOpen, setFilePickerOpen] = useState(false);
  const [pickerCollection, setPickerCollection] = useState('');
  const { data: storageCollections } = useGetStorageCollectionsQuery();
  const { data: pickerFiles } = useGetStorageFilesQuery(
    { collectionId: pickerCollection || undefined },
    { skip: !pickerCollection },
  );

  // ── Control dialogs ────────────────────────────────────────────────────
  const [stopMode, setStopMode] = useState<'soft' | 'hard'>('soft');
  const [compactOpen, setCompactOpen] = useState(false);
  const [compactPrompt, setCompactPrompt] = useState('');
  const [forkOpen, setForkOpen] = useState(false);
  const [forkPosition, setForkPosition] = useState(0);
  const [forkModel, setForkModel] = useState('');
  const [delegateOpen, setDelegateOpen] = useState(false);
  const [delegateRows, setDelegateRows] = useState<DelegateRow[]>([newDelegateRow()]);
  const [versionSnap, setVersionSnap] = useState<MessageVersion | null>(null);

  const maxPosition = Math.max(0, messages.length - 1);

  const handleSend = async () => {
    if (!sendText.trim() && fileRefs.length === 0) return;
    try {
      await addMessage({
        id,
        body: {
          content: sendText.trim() || undefined,
          fileRefs: fileRefs.length > 0 ? fileRefs : undefined,
        },
      }).unwrap();
      setSendText('');
      setFileRefs([]);
      refetchRef.current();
    } catch (err) {
      toastError((err as any)?.data?.message || 'Failed to send message');
    }
  };

  const handleCompact = async () => {
    try {
      await compactConversation({ id, prompt: compactPrompt.trim() || undefined }).unwrap();
      toastSuccess('Compaction requested');
      setCompactOpen(false);
      setCompactPrompt('');
      refetchRef.current();
    } catch {
      toastError('Failed to request compaction');
    }
  };

  const handleFork = async () => {
    try {
      const result = await forkConversation({
        id,
        body: {
          messagePosition: forkPosition,
          modelConfigId: forkModel || undefined,
        },
      }).unwrap();
      const cid =
        (result as any)?.conversationId ?? (result as any)?.data?.conversationId ?? (result as any)?.data?._id;
      toastSuccess('Conversation forked');
      setForkOpen(false);
      if (cid) navigate(`/conversations/${cid}`);
    } catch (err) {
      toastError((err as any)?.data?.message || 'Failed to fork conversation');
    }
  };

  const handleDelegate = async () => {
    const targets: Array<{ factoryId: string; payload?: Record<string, unknown> }> = [];
    for (const row of delegateRows.filter((r) => r.factoryId)) {
      if (!row.payload.trim()) {
        targets.push({ factoryId: row.factoryId });
        continue;
      }
      try {
        const payload = tryParseJson(row.payload) as Record<string, unknown>;
        targets.push({ factoryId: row.factoryId, payload });
      } catch {
        toastError('Delegate target payload is not valid JSON — fix or clear it');
        return;
      }
    }
    if (targets.length === 0) {
      toastError('Add at least one delegate target');
      return;
    }
    try {
      const result = await delegateConversation({
        id,
        body: { targets, mode: 'bulk' },
      }).unwrap();
      const subIds = (result as any)?.subConversationIds ?? [];
      toastSuccess(`Delegated to ${targets.length} target${targets.length > 1 ? 's' : ''}`);
      setDelegateOpen(false);
      if (subIds.length === 1) navigate(`/conversations/${subIds[0]}`);
    } catch (err) {
      toastError((err as any)?.data?.message || 'Failed to delegate');
    }
  };

  const handleModelOverride = async (modelConfigId: string) => {
    try {
      await setConversationModel({ id, modelConfigId }).unwrap();
      toastSuccess('Model override applied');
      refetchRef.current();
    } catch {
      toastError('Failed to set model override');
    }
  };

  const handleComplete = async () => {
    try {
      await completeConversation(id).unwrap();
      toastSuccess('Conversation completed');
      refetchRef.current();
    } catch {
      toastError('Failed to complete conversation');
    }
  };

  const togglePickFile = (file: { _id: string; fileName: string; mimeType?: string }) => {
    const already = fileRefs.some((f) => f.fileId === file._id);
    if (already) {
      setFileRefs(fileRefs.filter((f) => f.fileId !== file._id));
    } else {
      setFileRefs([
        ...fileRefs,
        {
          fileId: file._id,
          mediaType: file.mimeType ?? 'application/octet-stream',
          inject: true,
        },
      ]);
    }
  };

  const runsColumns: ColumnDef<Run, unknown>[] = [
    {
      accessorKey: 'seq',
      header: 'Seq',
      cell: ({ row }) => (
        <span className="tabular-nums">#{row.original.seq}</span>
      ),
    },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: ({ row }) => (
        <Badge variant={RUN_VARIANT[row.original.status] ?? 'outline'}>{row.original.status}</Badge>
      ),
    },
    {
      accessorKey: 'steps',
      header: 'Steps',
      cell: ({ row }) => (
        <span className="tabular-nums">{row.original.steps}</span>
      ),
    },
    {
      accessorKey: 'usage',
      header: 'Usage',
      cell: ({ row }) => (
        <span className="tabular-nums">
          {formatTokens((row.original.usage as { totalTokens?: number } | undefined)?.totalTokens)}
        </span>
      ),
    },
    {
      accessorKey: 'error',
      header: 'Error (catalog code)',
      cell: ({ row }) =>
        row.original.error ? (
          <div className="flex items-center gap-1.5">
            <Badge variant="destructive" className="font-mono text-xs">{row.original.error.code}</Badge>
            <span className="text-xs text-destructive line-clamp-1">{row.original.error.message}</span>
          </div>
        ) : (
          <span className="text-muted-foreground">—</span>
        ),
    },
    {
      accessorKey: 'startedAt',
      header: 'Started',
      cell: ({ row }) =>
        row.original.startedAt
          ? format(new Date(row.original.startedAt), 'MMM d, HH:mm:ss')
          : '—',
    },
  ];

  if (isLoading) {
    return (
      <div className="flex min-h-[50vh] items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (isError || !conversation) {
    return (
      <div className="space-y-4">
        <Button variant="ghost" size="icon" onClick={() => navigate('/conversations')}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <p className="pt-8 text-center text-destructive">Conversation not found or failed to load.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-center gap-3">
        <Button variant="ghost" size="icon" onClick={() => navigate('/conversations')}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div className="min-w-0 flex-1">
          <h1 className="flex flex-wrap items-center gap-2 text-2xl font-bold">
            <span className="truncate">{conversation.name || conversation._id}</span>
            <Badge variant={STATUS_VARIANT[conversation.status] ?? 'outline'}>
              {conversation.status}
            </Badge>
            {conversation.modelOverride && (
              <Badge variant="warning">override: {conversation.modelOverride.reason}</Badge>
            )}
          </h1>
          <p className="flex items-center gap-2 truncate text-xs text-muted-foreground">
            <span className="font-mono">{conversation._id}</span>
            <span>·</span>
            <span>{models?.find((m) => m._id === (conversation.modelOverride?.modelConfigId ?? conversation.modelConfigId))?.name ?? 'model'}</span>
          </p>
        </div>
        <div className="flex items-center gap-1.5">
          <Button variant="ghost" size="icon" title={wsConnected ? 'Stream connected' : 'Stream reconnecting…'}>
            <MessageSquare className={wsConnected ? 'h-4 w-4 text-green-600' : 'h-4 w-4 animate-pulse text-amber-500'} />
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="gap-1.5"
            disabled={completing}
            onClick={handleComplete}
          >
            <CheckCircle2 className="h-4 w-4" /> Complete
          </Button>
        </div>
      </div>

      {/* Controls bar */}
      <Card className="py-3">
        <CardContent className="flex flex-wrap items-center gap-2">
          <Select value={stopMode} onValueChange={(v) => setStopMode(v as 'soft' | 'hard')}>
            <SelectTrigger className="w-28">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="soft">soft</SelectItem>
              <SelectItem value="hard">hard</SelectItem>
            </SelectContent>
          </Select>
          <ConversationControls
            conversationId={id}
            status={conversation.status}
            control={conversation.control}
            stopMode={stopMode}
            onChanged={() => refetchRef.current()}
          />
          <Button
            variant="outline"
            size="sm"
            disabled={compacting}
            onClick={() => setCompactOpen(true)}
          >
            <Minimize2 className="mr-1.5 h-3.5 w-3.5" /> Compact
          </Button>
          <Button variant="outline" size="sm" onClick={() => { setForkPosition(maxPosition); setForkOpen(true); }}>
            <GitFork className="mr-1.5 h-3.5 w-3.5" /> Fork
          </Button>
          <Button variant="outline" size="sm" onClick={() => setDelegateOpen(true)}>
            <Share2 className="mr-1.5 h-3.5 w-3.5" /> Delegate
          </Button>
          <Separator orientation="vertical" className="h-6" />
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground">Model:</span>
            <Select
              value={conversation.modelOverride?.modelConfigId ?? conversation.modelConfigId}
              onValueChange={handleModelOverride}
            >
              <SelectTrigger className="w-48">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {(models ?? []).map((m) => (
                  <SelectItem key={m._id} value={m._id}>{m.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Conversation body */}
      <div className="grid grid-cols-1 gap-6 xl:grid-cols-3">
        {/* Messages */}
        <div className="space-y-4 xl:col-span-2">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2 text-base">
                <MessageSquare className="h-4 w-4 text-muted-foreground" /> Messages
                <span className="text-xs font-normal text-muted-foreground">({messages.length})</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="max-h-[50vh] space-y-3 overflow-y-auto p-4">
              {messages.length === 0 ? (
                <p className="py-8 text-center text-sm text-muted-foreground">
                  No messages yet. Send the first message below.
                </p>
              ) : (
                messages.map((m) => <MessageBubble key={`${m._id}-${m.seq}`} msg={m} />)
              )}
            </CardContent>
          </Card>

          {/* Composer */}
          <Card>
            <CardContent className="p-3">
              <div className="flex items-end gap-2">
                <Textarea
                  value={sendText}
                  onChange={(e) => setSendText(e.target.value)}
                  placeholder="Send a message to the agent…"
                  rows={3}
                  className="resize-none"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
                      e.preventDefault();
                      handleSend();
                    }
                  }}
                />
                <div className="flex flex-col gap-1">
                  <Button
                    variant="outline"
                    size="icon"
                    title="Attach storage files"
                    onClick={() => setFilePickerOpen(true)}
                  >
                    <Paperclip className="h-4 w-4" />
                  </Button>
                  <Button
                    size="icon"
                    title="Send (Ctrl+Enter)"
                    disabled={sending || (!sendText.trim() && fileRefs.length === 0)}
                    onClick={handleSend}
                  >
                    {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                  </Button>
                </div>
              </div>
              {fileRefs.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {fileRefs.map((f) => (
                    <span
                      key={f.fileId}
                      className="inline-flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 font-mono text-xs"
                    >
                      {f.fileId.slice(0, 10)}…({f.mediaType})
                      <button
                        type="button"
                        onClick={() => setFileRefs(fileRefs.filter((x) => x.fileId !== f.fileId))}
                        className="text-muted-foreground hover:text-destructive"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Side column: thinking panel + tabs */}
        <div className="space-y-4 min-w-0">
          {/* Thinking panel (AC-13) */}
          <Card className={thinkingOpen && hasReasoning ? 'bg-muted/20' : ''}>
            <CardHeader className="pb-2">
              <button
                type="button"
                className="flex w-full items-center gap-2 text-left"
                onClick={() => setThinkingOpen(!thinkingOpen)}
              >
                <Brain className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm font-semibold">Thinking</span>
                {hasReasoning && !thinkingOpen && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                <Badge variant="outline" className="ml-auto text-xs">
                  step {thinking.step != null ? thinking.step + 1 : '—'}
                  {thinking.maxSteps != null ? `/${thinking.maxSteps}` : ''} · {formatTokens(thinking.tokens)} tok
                </Badge>
              </button>
            </CardHeader>
            {thinkingOpen && (
              <CardContent className="pt-0">
                {hasReasoning ? (
                  <div className="max-h-[32vh] overflow-y-auto whitespace-pre-wrap rounded-md border border-border/60 bg-muted/40 p-3 font-mono text-xs leading-relaxed text-muted-foreground">
                    {thinking.reasoning || 'Streaming thoughts…'}
                    {wsConnected && hasReasoning && <span className="ml-0.5 inline-block h-3 w-1.5 animate-pulse bg-muted-foreground align-middle" />}
                  </div>
                ) : (
                  <p className="rounded-md border border-dashed p-3 text-xs text-muted-foreground">
                    No reasoning streamed yet{wsConnected ? '' : ` (${wsStateLabel})`}.
                  </p>
                )}
              </CardContent>
            )}
          </Card>

          {/* Tabs: Runs / Versions / Ephemeral */}
          <Tabs defaultValue="runs">
            <TabsList className="w-full">
              <TabsTrigger value="runs" className="flex-1"><History className="mr-1 h-3.5 w-3.5" /> Runs</TabsTrigger>
              <TabsTrigger value="versions" className="flex-1"><Layers className="mr-1 h-3.5 w-3.5" /> Versions</TabsTrigger>
              <TabsTrigger value="ephemeral" className="flex-1"><Package className="mr-1 h-3.5 w-3.5" /> Ephemeral</TabsTrigger>
            </TabsList>

            <TabsContent value="runs">
              {runs.length === 0 ? (
                <p className="py-6 text-center text-sm text-muted-foreground">No runs yet.</p>
              ) : (
                <Card>
                  <CardContent className="p-0">
                    <div className="max-h-[40vh] overflow-auto">
                      <DataTable columns={runsColumns} data={runs} pageSize={10} />
                    </div>
                  </CardContent>
                </Card>
              )}
            </TabsContent>

            <TabsContent value="versions">
              {versions.length === 0 ? (
                <p className="py-6 text-center text-sm text-muted-foreground">No versions yet.</p>
              ) : (
                <div className="space-y-2">
                  {versions.map((v) => (
                    <div key={v._id} className="flex items-center gap-2 rounded-md border p-3">
                      <Layers className="h-4 w-4 text-muted-foreground" />
                      <span className="text-sm font-medium">v{v.versionNumber}</span>
                      <Badge variant="outline" className="text-xs">{v.reason}</Badge>
                      <span className="ml-auto text-xs text-muted-foreground">
                        {format(new Date(v.createdAt), 'MMM d, HH:mm')}
                      </span>
                      <Button variant="ghost" size="sm" onClick={() => setVersionSnap(v)}>
                        View snapshot
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </TabsContent>

            <TabsContent value="ephemeral">
              {(conversation.ephemeralResources ?? []).length === 0 ? (
                <p className="py-6 text-center text-sm text-muted-foreground">No ephemeral resources.</p>
              ) : (
                <div className="space-y-2">
                  {(conversation.ephemeralResources ?? []).map((r, i) => (
                    <div key={i} className="flex items-center gap-2 rounded-md border p-3">
                      <Badge variant="outline">{r.type}</Badge>
                      <span className="font-mono text-xs">{r.id}</span>
                      <Badge variant="secondary" className="ml-auto text-xs">{r.cleanupOn}</Badge>
                    </div>
                  ))}
                </div>
              )}
            </TabsContent>
          </Tabs>
        </div>
      </div>

      {/* ── File attach dialog ───────────────────────────────────────────── */}
      <Dialog open={filePickerOpen} onOpenChange={(open) => !open && setFilePickerOpen(false)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Attach storage files</DialogTitle>
            <DialogDescription>Pick a collection, then files. They are injected as file refs.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-2">
              <Label>Collection</Label>
              <Select value={pickerCollection} onValueChange={setPickerCollection}>
                <SelectTrigger>
                  <SelectValue placeholder="Select collection" />
                </SelectTrigger>
                <SelectContent>
                  {(storageCollections ?? []).map((c) => (
                    <SelectItem key={c._id} value={c._id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {pickerCollection && (
              <div className="max-h-56 space-y-1.5 overflow-y-auto rounded-md border p-2">
                {(pickerFiles ?? []).length === 0 ? (
                  <p className="p-2 text-xs text-muted-foreground">No files in this collection.</p>
                ) : (
                  (pickerFiles ?? []).map((f) => {
                    const checked = fileRefs.some((x) => x.fileId === f._id);
                    return (
                      <label key={f._id} className="flex cursor-pointer items-center gap-2 rounded px-2 py-1 text-sm hover:bg-muted/50">
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => togglePickFile(f)}
                          className="h-4 w-4 rounded border-input accent-primary"
                        />
                        <span className="min-w-0 flex-1 truncate">{f.fileName}</span>
                        <span className="shrink-0 text-xs text-muted-foreground">{(f.size ?? 0).toLocaleString()} B</span>
                      </label>
                    );
                  })
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button
              onClick={() => {
                setFilePickerOpen(false);
                setPickerCollection('');
              }}
            >
              Done ({fileRefs.length} attached)
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Compact dialog ───────────────────────────────────────────────── */}
      <Dialog open={compactOpen} onOpenChange={setCompactOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Request compaction</DialogTitle>
            <DialogDescription>
              Summarize the conversation so far. Optionally override the factory compaction prompt.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="compact-prompt">Prompt override</Label>
            <Textarea
              id="compact-prompt"
              value={compactPrompt}
              onChange={(e) => setCompactPrompt(e.target.value)}
              placeholder="Optional override…"
              rows={4}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCompactOpen(false)}>Cancel</Button>
            <Button onClick={handleCompact} disabled={compacting}>
              {compacting ? 'Requesting…' : 'Compact'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Fork dialog ──────────────────────────────────────────────────── */}
      <Dialog open={forkOpen} onOpenChange={setForkOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Fork conversation</DialogTitle>
            <DialogDescription>
              Create a new conversation snapshot at a message position.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>
                Message position: #{forkPosition}{messages[forkPosition] ? ` (${messages[forkPosition].kind}, seq ${messages[forkPosition].seq})` : ''}
              </Label>
              <input
                type="range"
                min={0}
                max={maxPosition}
                value={forkPosition}
                onChange={(e) => setForkPosition(Number(e.target.value))}
                className="w-full accent-primary"
              />
              <div className="flex justify-between text-xs text-muted-foreground">
                <span>#0</span>
                <span>#{maxPosition}</span>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Model override (optional)</Label>
              <Select value={forkModel} onValueChange={setForkModel}>
                <SelectTrigger>
                  <SelectValue placeholder="Inherit current" />
                </SelectTrigger>
                <SelectContent>
                  {(models ?? []).map((m) => (
                    <SelectItem key={m._id} value={m._id}>{m.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setForkOpen(false)}>Cancel</Button>
            <Button onClick={handleFork} disabled={forking || messages.length === 0}>
              {forking ? 'Forking…' : 'Fork'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Delegate dialog ──────────────────────────────────────────────── */}
      <Dialog open={delegateOpen} onOpenChange={setDelegateOpen}>
        <DialogContent className="max-w-xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Delegate conversation</DialogTitle>
            <DialogDescription>
              Fan-out this conversation to multiple factories with optional payloads (mode: bulk).
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {(delegateRows ?? []).map((row, index) => (
              <div key={row.key} className="space-y-2 rounded-md border p-3">
                <div className="flex items-end gap-2">
                  <div className="min-w-[11rem] flex-1 space-y-1">
                    <span className="text-xs text-muted-foreground">Target factory</span>
                    <Select
                      value={row.factoryId}
                      onValueChange={(v) =>
                        setDelegateRows((prev) =>
                          prev.map((r) => (r.key === row.key ? { ...r, factoryId: v } : r)),
                        )
                      }
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select factory" />
                      </SelectTrigger>
                      <SelectContent>
                        {(factories ?? []).map((f) => (
                          <SelectItem key={f._id} value={f._id}>{f.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="text-destructive"
                    disabled={delegateRows.length === 1}
                    onClick={() =>
                      setDelegateRows((prev) => prev.filter((r) => r.key !== row.key))
                    }
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
                <JsonField
                  label="Payload (JSON, optional)"
                  value={row.payload}
                  onChange={(v) =>
                    setDelegateRows((prev) =>
                      prev.map((r) => (r.key === row.key ? { ...r, payload: v } : r)),
                    )
                  }
                  height={100}
                />
              </div>
            ))}
            <Button
              variant="outline"
              size="sm"
              onClick={() => setDelegateRows((prev) => [...prev, newDelegateRow()])}
            >
              Add target
            </Button>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDelegateOpen(false)}>Cancel</Button>
            <Button onClick={handleDelegate} disabled={delegating}>
              {delegating ? 'Delegating…' : 'Delegate'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Version snapshot viewer ──────────────────────────────────────── */}
      <Dialog open={!!versionSnap} onOpenChange={(open) => !open && setVersionSnap(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Version v{versionSnap?.versionNumber} snapshot</DialogTitle>
            <DialogDescription>
              Reason: {versionSnap?.reason} · {versionSnap ? format(new Date(versionSnap.createdAt), 'MMM d, yyyy HH:mm:ss') : ''}
            </DialogDescription>
          </DialogHeader>
          <JsonViewer data={versionSnap?.messages ?? []} />
          <DialogFooter>
            <Button onClick={() => setVersionSnap(null)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}